/**
 * Returns the MDI icon string for a placed overlay item, derived from its type and unit-of-measure.
 * @deprecated use getEquipmentSvg() for SVG-based icons
 */
export function getItemIcon(item: {
  type?: string;
  unitOfMeasure?: string;
}): string {
  if (item.type === 'end-use') return 'mdi-lightning-bolt-outline';

  const uom = item.unitOfMeasure ?? '';
  if (/celsius/i.test(uom)) return 'mdi-temperature-celsius';
  if (/fahrenheit/i.test(uom)) return 'mdi-temperature-fahrenheit';
  if (/temperature/i.test(uom)) return 'mdi-thermometer';
  if (/gallon/i.test(uom)) return 'mdi-water';
  if (/watt|kwh|kw\b|power|energy/i.test(uom)) return 'mdi-lightning-bolt';
  if (/psi|pressure/i.test(uom)) return 'mdi-gauge';
  if (/ampere|amp\b|current/i.test(uom)) return 'mdi-current-ac';
  if (/volt/i.test(uom)) return 'mdi-flash-triangle';
  if (/percent|humidity|rh\b/i.test(uom)) return 'mdi-water-percent';

  return 'mdi-chip';
}

/**
 * Maps a raw unit-of-measure string (as returned by the API) to a short display
 * abbreviation. Regex patterns mirror those used in getItemIcon() so both
 * functions stay in sync when new UOM categories are added.
 *
 * Examples:
 *   "Degrees Fahrenheit" → "ºF"
 *   "Kilowatt Hour"      → "kWh"
 *   "Pounds Per Sq In"   → "PSI"
 */
export function formatUnitOfMeasure(uom: string | undefined): string {
  if (!uom) return '';
  const u = uom.trim();

  if (/celsius/i.test(u)) return 'ºC';
  if (/fahrenheit/i.test(u)) return 'ºF';
  if (/temperature/i.test(u)) return 'ºC';

  if (/gallon/i.test(u)) return 'Gal';

  if (/kwh|kilowatt[\s-]?hour/i.test(u)) return 'kWh';
  if (/kw\b|kilowatt/i.test(u)) return 'kW';
  if (/watt/i.test(u)) return 'W';
  if (/energy/i.test(u)) return 'kWh';
  if (/power/i.test(u)) return 'kW';

  if (/psi/i.test(u)) return 'PSI';
  if (/pressure/i.test(u)) return 'PSI';

  if (/ampere|amp\b|current/i.test(u)) return 'A';
  if (/volt/i.test(u)) return 'V';

  if (/humidity|rh\b/i.test(u)) return '%RH';
  if (/percent/i.test(u)) return '%';

  return u;
}

// ---------------------------------------------------------------------------
// Sites endpoint (API contract — GET /digital-twins/sites)
// ---------------------------------------------------------------------------

export interface IDigitalTwinSite {
  id: number;
  siteId: number;
  siteName: string;
}

// ---------------------------------------------------------------------------
// Equipment types (API contract — GET /digital-twins/equipment?siteId={id})
// ---------------------------------------------------------------------------

/** Equipment item returned by GET /digital-twins/equipment?siteId={id} */
export interface IEquipment {
  equipmentId: number;
  equipmentName: string;
  equipmentTypeId: number;
  equipmentTypeName: string;
}

/**
 * Single reading row returned by GET /digital-twins/latest-readings
 * An equipment item may have multiple readings (sensors + end-uses mixed).
 */
export interface IEquipmentReading {
  equipmentId: number;
  equipmentName: string;
  readingName: string;
  readingValue: number;
  readingUoM: string;
  readingType: 'Sensor' | 'Enduse';
}

/** Equipment item bundled with its latest readings — used in EquipmentModal peer cards. */
export interface IEquipmentGroup extends IEquipment {
  readings: IEquipmentReading[];
}

/** API response shape from GET /digital-twins/equipment?siteId={id} — grouped by site system. */
export interface IEquipmentSystemGroup {
  siteSystemId: number;
  siteSystemName: string;
  equipments: IEquipment[];
}

/**
 * SVG filenames available under /public/images/digital-twin/.
 * The API returns equipmentTypeName values that match these names exactly.
 */
const KNOWN_SVG_FILES = new Set([
  'Condenser',
  'CWMani90',
  'CWMix',
  'CWSupply',
  'Dishwasher',
  'ElectricalTransformer',
  'ElectricPanel',
  'FlowSensor',
  'HVAC',
  'HVACRoof',
  'HWMix',
  'HWSupply',
  'IAQ',
  'LightSwitch',
  'OccSensor',
  'PressureSensor',
  'Pump',
  'Split',
  'Submeter',
  'TempSensor',
  'Thermostat',
  'WashingMachine',
  'WaterHeater',
  'WaterHeaterTankless',
]);

/** Legacy aliases for overlay items persisted before the API used canonical type names. */
const LEGACY_TYPE_SVG_MAP: Record<string, string> = {
  'mixing valve': '/images/digital-twin/CWMix.svg',
  mixingvalve: '/images/digital-twin/CWMix.svg',
  manifold: '/images/digital-twin/CWMani90.svg',
  split: '/images/digital-twin/Split.svg',
  'split ac': '/images/digital-twin/Split.svg',
  splitac: '/images/digital-twin/Split.svg',
  // Water heating
  'water heater': '/images/digital-twin/WaterHeater.svg',
  waterheater: '/images/digital-twin/WaterHeater.svg',
  'heat pump water heater': '/images/digital-twin/WaterHeater.svg',
  heatpumpwaterheater: '/images/digital-twin/WaterHeater.svg',
  'tankless water heater': '/images/digital-twin/WaterHeaterTankless.svg',
  tanklesswaterheater: '/images/digital-twin/WaterHeaterTankless.svg',
  // Water loops & supply
  'hot water loop': '/images/digital-twin/HWMix.svg',
  hotwaterloop: '/images/digital-twin/HWMix.svg',
  'hot water supply': '/images/digital-twin/HWSupply.svg',
  hotwatersupply: '/images/digital-twin/HWSupply.svg',
  'chilled water supply': '/images/digital-twin/CWSupply.svg',
  chilledwatersupply: '/images/digital-twin/CWSupply.svg',
  // Sensors
  thermostat: '/images/digital-twin/Thermostat.svg',
  'temp sensor': '/images/digital-twin/TempSensor.svg',
  tempsensor: '/images/digital-twin/TempSensor.svg',
  'temperature sensor': '/images/digital-twin/TempSensor.svg',
  temperaturesensor: '/images/digital-twin/TempSensor.svg',
  'flow sensor': '/images/digital-twin/FlowSensor.svg',
  flowsensor: '/images/digital-twin/FlowSensor.svg',
  'pressure sensor': '/images/digital-twin/PressureSensor.svg',
  pressuresensor: '/images/digital-twin/PressureSensor.svg',
  'occ sensor': '/images/digital-twin/OccSensor.svg',
  occsensor: '/images/digital-twin/OccSensor.svg',
  'occupancy sensor': '/images/digital-twin/OccSensor.svg',
  occupancysensor: '/images/digital-twin/OccSensor.svg',
  iaq: '/images/digital-twin/IAQ.svg',
  'iaq sensor': '/images/digital-twin/IAQ.svg',
  iaqsensor: '/images/digital-twin/IAQ.svg',
  // Electrical
  submeter: '/images/digital-twin/Submeter.svg',
  'electric panel': '/images/digital-twin/ElectricPanel.svg',
  electricpanel: '/images/digital-twin/ElectricPanel.svg',
  'electrical transformer': '/images/digital-twin/ElectricalTransformer.svg',
  electricaltransformer: '/images/digital-twin/ElectricalTransformer.svg',
  'light switch': '/images/digital-twin/LightSwitch.svg',
  lightswitch: '/images/digital-twin/LightSwitch.svg',
  // Appliances
  'laundry machine': '/images/digital-twin/WashingMachine.svg',
  laundrymachine: '/images/digital-twin/WashingMachine.svg',
  dishwasher: '/images/digital-twin/Dishwasher.svg',
};

/**
 * Returns the public SVG path for a given equipment type, or null if not found.
 * Tries the exact API type name first (matches SVG filename), then falls back to
 * legacy aliases for overlay items persisted with older names.
 */
export function getEquipmentSvg(
  equipmentType: string | undefined | null
): string | null {
  if (!equipmentType) return null;
  const t = equipmentType.trim();
  if (KNOWN_SVG_FILES.has(t)) return `/images/digital-twin/${t}.svg`;
  return LEGACY_TYPE_SVG_MAP[t.toLowerCase()] ?? null;
}

export interface ISegment {
  id: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  color: string;
  style: 'solid' | 'dashed';
  hasArrow: boolean;
}

export interface IOverlayItem {
  id: string;
  /** Equipment type string — drives SVG icon lookup via getEquipmentSvg(). May be absent on legacy persisted items. */
  equipmentType?: string;
  itemId: number;
  name: string;
  /** Site system this equipment belongs to — stored so the overlay can be grouped by system on read. Optional for backward compat with overlays persisted before this field was added. */
  siteSystemId?: number;
  siteSystemName?: string;
  /** Percentage (0–100) of the canvas width  */
  x: number;
  /** Percentage (0–100) of the canvas height */
  y: number;
  /** Per-item scale multiplier applied on top of the global iconSize (default 1 when absent). */
  scale?: number;
}

export interface IDigitalTwin {
  id: number;
  siteSystemId: number;
  /** Base64-encoded SVG data URI, e.g. 'data:image/svg+xml;base64,...' */
  baseLayout: string;
  overlay: IOverlayItem[];
  segments: ISegment[];
  displayOrder: number;
}

/** Wire format returned by GET /digital-twins?siteId={id} (overlay is a serialised JSON string). */
export interface IDigitalTwinDTO {
  id: number;
  siteSystemId: number;
  baseLayout: string;
  /** Serialised JSON string of IOverlayItem[] */
  overlay: string;
  displayOrder: number;
}

/** Body sent to PUT /v1/digitaltwins/{id} (update). Transmitted as multipart/form-data. */
export interface IUpdateDigitalTwinRequest {
  /** SVG file — optional; omit to keep the existing layout */
  baseLayout?: File;
  /** Serialised JSON string of IOverlayItem[] */
  overlay: string;
  displayOrder: number;
}
